import { useEffect, useState, type ReactElement } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Star, Mail, Phone, MapPin, FileText, User, FileSignature, Receipt } from 'lucide-react';
import { customerApi, companyApi, type Customer, type CustomerStats, type CustomerInvoiceRow, type CustomerReceiptRow } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useToast, errMsg } from '../ToastProvider';
import { Card, PageHeader, euro, displayCustomer, StatusBadge } from '../ui';

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('de-DE');
}

function parseTags(s: string | null | undefined): string[] {
  return (s ?? '').split(',').map((t) => t.trim()).filter(Boolean);
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' | undefined }): ReactElement {
  return (
    <Card className="!p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`mt-1 text-xl font-bold ${tone === 'good' ? 'text-emerald-600' : tone === 'bad' ? 'text-rose-600' : 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
    </Card>
  );
}

export default function CustomerDetail(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const { active } = useCompany();
  const toast = useToast();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [invoices, setInvoices] = useState<CustomerInvoiceRow[]>([]);
  const [quotes, setQuotes] = useState<CustomerInvoiceRow[]>([]);
  const [receipts, setReceipts] = useState<CustomerReceiptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (!active) return;
    companyApi.usage(active.id).then((u) => setLocked(!u.features.includes('kundenverwaltung'))).catch(() => undefined);
  }, [active]);

  useEffect(() => {
    if (!active || !id) return;
    setLoading(true);
    Promise.all([customerApi.get(active.id, id), customerApi.stats(active.id, id)])
      .then(([c, s]) => { setCustomer(c); setStats(s.stats); setInvoices(s.invoices); setQuotes(s.quotes); setReceipts(s.receipts); })
      .catch((e) => { if (!locked) toast.error(errMsg(e)); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, id]);

  if (locked) {
    return (
      <div className="mx-auto max-w-4xl">
        <button onClick={() => navigate('/app/kunden')} className="mb-3 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" /> Alle Kontakte
        </button>
        <PageHeader title="Kundendetails" subtitle="Umsatz, Zahlungsverhalten und Historie pro Kunde." />
        <Card className="text-center">
          <p className="text-gray-700 dark:text-gray-300">🔒 Die erweiterte Kundenübersicht ist ab dem <strong>Plus</strong>-Tarif verfügbar.</p>
          <Link to="/app/einstellungen/tarif" className="mt-2 inline-block font-medium text-blue-600 hover:underline">Tarif ansehen</Link>
        </Card>
      </div>
    );
  }

  if (loading) return <p className="mx-auto max-w-4xl text-gray-500">Lädt …</p>;
  if (!customer) return <p className="mx-auto max-w-4xl text-gray-500">Kunde nicht gefunden.</p>;

  const tags = parseTags(customer.tags);
  const kind = customer.kind ?? 'customer';
  const showCustomer = kind === 'customer' || kind === 'both';
  const showSupplier = kind === 'supplier' || kind === 'both';

  return (
    <div className="mx-auto max-w-4xl">
      <button onClick={() => navigate('/app/kunden')} className="mb-3 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" /> Alle Kontakte
      </button>

      <PageHeader
        title={(
          <span className="inline-flex items-center gap-2">
            {customer.vip && <Star className="h-5 w-5 fill-amber-400 text-amber-400" />}
            {displayCustomer(customer)}
            {customer.customerNumber && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-500">{customer.customerNumber}</span>
            )}
            {customer.active === false && (
              <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500">inaktiv</span>
            )}
          </span>
        )}
        subtitle={[customer.category, customer.industry, customer.region].filter(Boolean).join(' · ') || undefined}
      />

      {/* Kennzahlen */}
      {stats && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {showCustomer && <Stat label="Umsatz (bezahlt)" value={euro(stats.revenueCents)} tone="good" />}
          {showCustomer && <Stat label="Offene Forderungen" value={euro(stats.openCents)} tone={stats.openCents > 0 ? 'bad' : undefined} />}
          {showCustomer && <Stat label="Ø Zahlungsdauer" value={stats.avgPaymentDays !== null ? `${stats.avgPaymentDays} Tage` : '—'} />}
          {showCustomer && <Stat label="Rechnungen" value={`${stats.invoiceCount}`} />}
          {showSupplier && <Stat label="Ausgaben (Belege)" value={euro(stats.expenseCents)} tone={stats.expenseCents > 0 ? 'bad' : undefined} />}
          {showSupplier && <Stat label="Belege" value={`${stats.receiptCount}`} />}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Stammdaten */}
        <Card className="space-y-3 lg:col-span-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Stammdaten</h3>
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            {customer.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-gray-400" /> <a href={`mailto:${customer.email}`} className="text-blue-600 hover:underline">{customer.email}</a></div>}
            {customer.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" /> {customer.phone}</div>}
            {customer.contactPerson && <div className="flex items-center gap-2"><User className="h-4 w-4 text-gray-400" /> {customer.contactPerson}</div>}
            {(customer.street || customer.city) && (
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div>
                  {customer.street && <div>{customer.street}</div>}
                  <div>{[customer.postalCode, customer.city].filter(Boolean).join(' ')}</div>
                  {customer.country && customer.country !== 'Deutschland' && <div>{customer.country}</div>}
                </div>
              </div>
            )}
            {customer.vatId && <div className="text-xs text-gray-500">USt-IdNr.: {customer.vatId}</div>}
            <div className="text-xs text-gray-500">Zahlungsziel: {customer.paymentTerms} Tage</div>
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {tags.map((t) => <span key={t} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">#{t}</span>)}
            </div>
          )}
          {customer.notes && (
            <div className="rounded-lg bg-gray-50 p-2 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">{customer.notes}</div>
          )}
        </Card>

        {/* Rechnungen + Angebote + Belege */}
        <div className="space-y-4 lg:col-span-2">
          {showCustomer && (
          <Card className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Rechnungen</h3>
              {stats && stats.overdueCount > 0 && (
                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">{stats.overdueCount} überfällig</span>
              )}
            </div>
            {invoices.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">Noch keine Rechnungen für diesen Kunden.</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {invoices.map((inv) => (
                  <Link
                    key={inv.id}
                    to={`/app/rechnungen/${inv.id}`}
                    className="flex items-center justify-between gap-2 py-2 transition hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{inv.number ?? 'Entwurf'}</div>
                        <div className="text-xs text-gray-500">{fmtDate(inv.issueDate)}</div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <StatusBadge status={inv.status} />
                      <span className="w-24 text-right text-sm font-medium text-gray-900 dark:text-gray-100">{euro(inv.grossCents)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
          )}

          {/* Angebote — nur anzeigen, wenn vorhanden */}
          {showCustomer && quotes.length > 0 && (
            <Card className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Angebote</h3>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {quotes.map((q) => (
                  <Link
                    key={q.id}
                    to={`/app/rechnungen/${q.id}`}
                    className="flex items-center justify-between gap-2 py-2 transition hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <FileSignature className="h-4 w-4 shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{q.number ?? 'Entwurf'}</div>
                        <div className="text-xs text-gray-500">
                          {fmtDate(q.issueDate)}{q.validUntil ? ` · gültig bis ${fmtDate(q.validUntil)}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <StatusBadge status={q.status} />
                      <span className="w-24 text-right text-sm font-medium text-gray-900 dark:text-gray-100">{euro(q.grossCents)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}

          {/* Belege (Lieferant) */}
          {showSupplier && (
            <Card className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Belege</h3>
              {receipts.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">Noch keine Belege diesem Lieferanten zugeordnet.</p>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {receipts.map((r) => (
                    <Link
                      key={r.id}
                      to="/app/belege"
                      className="flex items-center justify-between gap-2 py-2 transition hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <Receipt className="h-4 w-4 shrink-0 text-gray-400" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{r.description || r.vendor || r.invoiceNumber || 'Beleg'}</div>
                          <div className="text-xs text-gray-500">{fmtDate(r.date)}{r.direction === 'income' ? ' · Einnahme' : ''}</div>
                        </div>
                      </div>
                      <span className="w-24 shrink-0 text-right text-sm font-medium text-gray-900 dark:text-gray-100">{euro(r.grossCents)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
